//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from '@mui/material/styles';
import {
  Box,
  CardHeader,
  Typography
} from '@mui/material';

// === CONSTANTS ===
import { FAILURE_CODE, SUCCESS_CODE } from "constants/general";

// === COMPONENTS ===
import { Spacer } from "components/Spacer";
import { BorderCorners } from "components/Border/components/BorderCorners";
import { TaskContext } from "pages/TaskVerification/components/TaskContext/TaskContext";

// === API ===
import { addTableRow, isFocusedField, onField, onFieldFocus, removeListener, removeTableRow, requestField, requestPreviousFieldFocus, setMainField, updateField, requestPageUp, requestPageDown,  requestThumbNavigation } from "api/prokey/prokey.websocket";
import { Pagetype , validate as validate_pagetype  } from './components/Pagetype/Pagetype';
import { Doctype  , validate as validate_doctype   } from './components/Doctype/Doctype';
import { Standard , validate as validate_standard  } from './components/Standard/Standard';
import { Selection, validate as validate_selection } from './components/Selection/Selection';
import { Amount   , validate, validate as validate_amount    } from './components/Amount/Amount';
import { Date     , validate as validate_date      } from './components/Date/Date';
import { MIN      , validate as validate_min       } from './components/MIN/MIN';


//#endregion

//-----------------------------------------------------------------
export function Field({id, timestamp}) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();
  const { images } = useContext( TaskContext );

  const [ data        , setData        ] = useState( {} );
  const [ content     , setContent     ] = useState( '' );
  const [ message     , setMessage     ] = useState( '' );
  const [ reference   , setReference   ] = useState( null );
  const [ has_focus   , setHasFocus    ] = useState( isFocusedField( id ) );

  // === SNIPPETS
  const snippets = useMemo( createSnippets, [data?.image, images] );
  const setting_snippets_enabled = toBoolean( localStorage.getItem( 'setting-snippets-enabled' ) || false );
  const setting_snippets_animate = toBoolean( localStorage.getItem( 'setting-snippets-animate' ) || true  );

  const header = data.DisplayInfo ? data.DisplayInfo.DisplayHeader : '' ;
  const length_char = data.DisplayInfo ? +data.DisplayInfo.Length : 50;
  const length = data.DisplayInfo ? Math.max(length_char * 12, 20 ) : '100%' ;
  const type   = data.DisplayInfo ?.Type;
  const alt_content       = data.DisplayInfo ? data.DisplayInfo.AltContent      || [] : [];
  const cross_doc_content = data.DisplayInfo ? data.DisplayInfo.CrossDocContent || [] : [];
  const selection_values  = [ ...alt_content, ...cross_doc_content ];
  const casing = data.DisplayInfo?.is_uppercase ? 'uppercase' : data.DisplayInfo?.is_lowercase ? 'lowercase' : '';

  const is_required      =  data.validationInfo?.isRequired;
  const is_validated     = (data.validationInfo?.isRejected || data.validationInfo?.isAccepted);
  const validation_color =  data.validationInfo?.isAccepted ? theme.palette.success.main : theme.palette.error.main;
  const border_color = is_validated ? validation_color : is_required ? theme.palette.status.review[theme.palette.mode] : theme.palette.status.certain.main;

  const is_visible = data.is_visible;

  let input_element = null;
  let input_validation = () => { return false };

  const setRefNode = useCallback(node => {
    if (node !== null) {
      setReference( {current: node} );
    }
  }, []);

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display   : is_visible ? '' : 'none',
      padding   : '1rem',
      flex      : 'auto',
      maxWidth  : '-webkit-fill-available',
      maxWidth  : '100%',
    },
    header: {
      padding: 0,
      paddingBottom: '0.5rem',
    },
    page_number:{
      paddingRight    : '1rem',     
      fontSize        : '17px',
      marginTop       : '0.7rem',
    },
    content: {
      width: '100%',
    },
    input: {
      borderRadius    : '5px',
      borderStyle     : 'solid',
      borderColor     : border_color,
      fontSize        : '15px',
      width           : length,
      height          : '30px',
      textTransform   : casing,
      paddingLeft     : '0.25rem',
      maxWidth        : '-webkit-fill-available',
      maxWidth        : '100%',
    },
  }

  const _props = {
    container: {
      sx: _styles.container,
      onClick: () => { setMainField(data); },
    },
    header: {
      sx: _styles.header,
      title: header,      
    },
    content: {
      sx: _styles.content,      
    },
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleField(data) {
      //-----------------------------------------------------------------
      setData(data);
      let value = data.Entries ? data.Entries[0].Content : '';
      setContent( value );
      setMessage( data.DisplayInfo.Message );
    }

    onField( id, handleField );

    requestField( id );
    return () => {
      removeListener( 'FIELD-' + id, handleField )
    }
  }, [id,timestamp] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      if ( data ) setData( data );
      setHasFocus( true );
      if( !reference ) return;
      reference.current.scrollIntoView({ behavior: "auto", block: "center", inline: "nearest" });

      const alreadyFocused = document.activeElement === reference.current;

      // If user clicked into the field, don't override their cursor position
      if (!alreadyFocused) {
        reference.current.focus();

        if (reference.current.select) {
          reference.current.select();
        }
      }
    }

    onFieldFocus( id, handleFieldFocus );

    return () => {
      removeListener( 'FIELD-FOCUS-' + id, handleFieldFocus )
    }
  }, [reference] );


  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onFocus(event) {
    //-----------------------------------------------------------------
    try {
      // If event.detail === 0 the focus came from keyboard navigation
      if (event?.detail === 0 && reference?.current?.select) {
        reference.current.select();
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onBlur() {
    //-----------------------------------------------------------------
    try {
      setHasFocus( false );
    } catch (error) {
      console.warn(error);
    }
  }
  //-----------------------------------------------------------------
  function toBoolean(str) {
    //-----------------------------------------------------------------
    if( str === true ) return true;
    const enabled = ['1', 'true', 'yes', 'ja', 'on'];
    return str && enabled.includes(str.toLowerCase());
  }
  
  //-----------------------------------------------------------------
  function onKeyDown( event ) {
    //-----------------------------------------------------------------
    // Allow Enter events to propagate to child input components.

    const is_action_prev = event.key === 'Tab' && event.shiftKey ;
    if( is_action_prev ) {
      event.preventDefault();
      event.stopPropagation();
      requestPreviousFieldFocus( id );
      return;
    }
      
    const is_action_tab = event.key === 'Tab'   && !event.shiftKey ;

    const is_action_page_up   = event.key === 'PageUp';
    const is_action_page_down = event.key === 'PageDown';

    if ( is_action_tab ) {
      // Stop default tab behavior
      event.preventDefault();
    }

    if( is_action_page_up ) {
      event.preventDefault();    
      requestThumbNavigation('prev');
      return;
    }

    if( is_action_page_down ) {
      event.preventDefault();
      requestThumbNavigation('next');      
      return;
    }

    // === TABLE COMMANDS ===
    if( data.isColumn ) {
      const is_action_create_row_above = event.key === 'ArrowUp'   && event.ctrlKey;
      const is_action_create_row_below = event.key === 'ArrowDown' && event.ctrlKey;
      const is_action_remove_row       = event.key === 'Delete'    && event.ctrlKey;
      if( is_action_create_row_above || is_action_create_row_below) {
        addTableRow( { field_id: id, isAfter: is_action_create_row_below } );
        event.preventDefault();
        return;
      }
      if( is_action_remove_row ) {
        removeTableRow( { field_id: id } );
        event.preventDefault();
        return;
      }
    }
  }
  
  //-----------------------------------------------------------------
  function confirm( actions={}, value ) {
    //-----------------------------------------------------------------
    const {
      is_action_force, is_action_enter, is_action_tab,
    } = actions;

    const ref_content = value;

    const force_accept    = data?.validationInfo?.ForceAccept;
    const is_force_empty  = ref_content === '' && force_accept?.toLowerCase() === 'empty';
    const is_force_filled = ref_content !== '' && force_accept?.toLowerCase() === 'filled';
    const is_force_never  = force_accept?.toLowerCase() === 'never';
    const is_force_always = force_accept?.toLowerCase() === 'always';   
    const is_forcable     = !is_force_never && (is_force_always || is_force_empty || is_force_filled);
    const is_forced       = is_action_force && is_forcable;
 
    const false_accept      = data?.validationInfo?.FalseAccept;
    const is_false_filled   = ref_content === '' && false_accept?.toLowerCase() === 'filled';
    const is_false_never    = ref_content === '' && false_accept?.toLowerCase() === 'never';
    const is_false_rejected = is_false_filled || is_false_never;

    const is_accepted     = is_action_force || is_action_enter || is_action_tab;

    const validation_result_common = validate_common( ref_content );
    const validation_result = is_accepted ? input_validation( ref_content, data.validationInfo, data.DisplayInfo.Type ) : {};
    const is_valid = validation_result_common && validation_result?.status === SUCCESS_CODE;

    const has_coords         = data.Entries[0].Bottom > 0;
    const is_coords_required = toBoolean( data?.validationInfo?.RequireCoords );
    const is_coords_failed   = is_coords_required && !has_coords;

    const is_forced_version = is_valid && is_action_force && data.Name === 'Version';

    // === VALIDATION ===
    if( is_forced || is_forced_version) {
      setMessage('Field has been force accepted!');

      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'FORCE', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }

    if( is_accepted && ref_content !== '' && is_coords_failed ) {
      setMessage( 'Field requires coordinates!' );
      data.validationInfo.isRejected = true;
      return { status: FAILURE_CODE, content: validation_result.content };
    }
    
    if( is_accepted && is_false_rejected ) {
      let message = 'Enter a value.'
      if( is_forcable ) message += ' [Field can be forced]';
      setMessage( message );
      data.validationInfo.isRejected = true;
      return { status: FAILURE_CODE, content: validation_result.content };
    }

    if( is_accepted && !is_valid ) {
      setMessage( validation_result.message );
      data.validationInfo.isRejected = true;
      return { status: FAILURE_CODE, content: validation_result.content };
    }

    if ( is_action_enter ) {
      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'ENTER', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }
    
    if ( is_action_tab ) {
      if( ref_content !== validation_result.content ) setContent( validation_result.content );
      updateField( 'TAB', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }
  }

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function getInputData() {
    //-----------------------------------------------------------------

    const STANDARD_PROPS = {
      name       : 'field_txt'   ,
      id         : id            ,
      style      : _styles.input ,
      field      : data          ,
      value      : content       ,
      has_focus  : has_focus     ,
      onFocus    : onFocus       ,
      onBlur     : onBlur        ,
      onKeyDown  : onKeyDown     ,
      inputRef   : setRefNode    ,
      setMessage : setMessage    ,
      confirm,
    }
  
    if( type === 'pagetype'  ) {
      input_validation = validate_pagetype;
      input_element = <Pagetype  { ...STANDARD_PROPS } />;
    }
    else if( type === 'doctype'   ) {
      input_validation = validate_doctype;
      input_element = <Doctype   { ...STANDARD_PROPS } />;
    }
    else if( type === 'selection' ) {
      input_validation = validate_selection;
      input_element = <Selection { ...STANDARD_PROPS } />;
    }
    else if( type === 'amount'    ) {
      input_validation = validate_amount;
      input_element = <Amount    { ...STANDARD_PROPS } />;
    }
    else if( type === 'date'      ) {
      input_validation = validate_date;
      input_element = <Date      { ...STANDARD_PROPS } />;
    }
    else if( type === 'min'      ) {
      input_validation = validate_min;
      input_element = <MIN       { ...STANDARD_PROPS } />;
    }
    else if( selection_values.length > 0 ) {
      input_validation = validate_selection;
      input_element = <Selection { ...STANDARD_PROPS } />;
    }
    else {
      input_validation = validate_standard;
      input_element = <Standard  { ...STANDARD_PROPS } />;
    }
  }

  //-----------------------------------------------------------------
  function createSnippets() {
    //-----------------------------------------------------------------
    try {
      if( data == undefined || data.Entries == undefined ) return;

      const results = [];
      for( let entry of data.Entries ) {
        const coords = getHighlightRect( entry );

        if( coords.width > 12000 || coords.width > 12000 ) {
          continue;
        }
        if( coords.width <= 0 || coords.width <= 0 ) {
          continue;
        }

        const image = images.values().find( x => x.page_number === parseInt( entry.PageNr ) );
        if( !image ) continue;

        const offset_x = 100;
        const offset_y = 100;

        const canvas = document.createElement('canvas');
        canvas.width  = coords.width  + offset_x;
        canvas.height = coords.height + offset_y;

        const offset_x_half = offset_x / 2;
        const offset_y_half = offset_y / 2;
        const context = canvas.getContext('2d');
        context.drawImage( image.html_image,
          coords.x-offset_x_half, coords.y-offset_y_half, coords.width+offset_x, coords.height+offset_y,
          0, 0, coords.width+offset_x, coords.height+offset_y
        );

        const color = true ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'

        context.globalCompositeOperation = "darken";

        context.beginPath();

        context.fillStyle = color;
        context.fillRect(offset_x_half, offset_y_half, coords.width, coords.height);

        context.closePath();

        results.push( canvas );
      }

      return results;

    } catch (error) {
      console.warn( {error} )
    }

    return [];
  }

  //-----------------------------------------------------------------
  function getHighlightRect(entry) {
    //-----------------------------------------------------------------
    if( entry.Left == undefined || entry.Top == undefined || entry.Left == undefined || entry.Left == undefined ) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    
    let skewedTopLeft     = skew(entry.Left , entry.Top   );
    let skewedBottomRight = skew(entry.Right, entry.Bottom);

    let skewedX      = parseInt(skewedTopLeft.x * 1);
    let skewedY      = parseInt(skewedTopLeft.y * 1);
    let skewedWidth  = parseInt(skewedBottomRight.x * 1) - skewedX;
    let skewedHeight = parseInt(skewedBottomRight.y * 1) - skewedY;
    
    return { x: skewedX, y: skewedY, width: skewedWidth, height: skewedHeight};
  }

  //-----------------------------------------------------------------
  function skew(x,y) {
    //-----------------------------------------------------------------
    let result = { x: parseInt(x), y: parseInt(y) };
    const slope = Math.tan( (data?.image.info.deskewAlpha*Math.PI)/180.0 );

    if( data?.image.info.deskewMode === '1' ) {
      // calculate x
      const y_start = slope > 0 ? 0 : data?.image.info.deskewHeight;
      const x_offset = slope * (y - y_start);
      const x_change = (Math.round(x_offset + 0.5)); // truncate decimal
      result.x -= x_change;
    }

    const y_change = ((slope * result.x + 0.5) << 0);
    result.y += y_change;

    return result;
  }

  //#endregion

  function validate_common (content) {
    let isvalid = true;

    // LenOption
    const lenOpt = data?.DisplayInfo?.LenOption?.toLowerCase();
    if(lenOpt) {
      // Strict Len - content must match field length exactly
      if(lenOpt.includes('s') && content.length != length_char){
        isvalid = false;
        setMessage( `Invalid field length, must be exactly ${length_char} characters` );
      }
      // Max Len - content length must not exceed field length
      else if(lenOpt.includes('f') && content.length > length_char){
        isvalid = false;
        setMessage( `Invalid field length, Max ${length_char} characters` );
      }
    }

    return isvalid;
  }

  //#region [RENDER]
 
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( data.Id === undefined ) return null;

    getInputData();
    return (
      <Box key={id} { ..._props.container }>
        <Box sx={_styles.header_container}>
          <CardHeader { ..._props.header  } action={displayPageNumber()}/>
        </Box>
        <Box { ..._props.content } >
          { input_element }
          { has_focus && message && (
            <Typography variant="body2" sx={{ marginTop: '4px', opacity: 0.8 }} >
              {message}
            </Typography>
          )}
        </Box>
        <Spacer size='0.5rem' />
        {displaySnippets()}
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displaySnippets() {
    //-----------------------------------------------------------------
    if( !snippets || snippets.length <= 0 || !images ) return null;
    if( !setting_snippets_enabled ) return null;

    return (
      <div style={{
        display    : 'inline flex' ,
        flexFlow   : 'row no-wrap' ,
        flexWrap   : 'wrap', }}>
        { snippets.map( ( snippet, index ) => displaySnippet( snippet, index ) ) }
      </div>
    )
  }

  //-----------------------------------------------------------------
  function displaySnippet(snippet, index) {
    //-----------------------------------------------------------------
    if( !snippet || !images ) return null;

    return (
      <BorderCorners key={index} color={theme.palette.primary.main} width={snippet?.width} height={snippet?.height} thickness='3px' animate={setting_snippets_animate} is_open={has_focus} topLeft={false} bottomRight={false}>
        <canvas ref={ (ref)=>drawCanvas(ref, snippet) }/>
      </BorderCorners>
    )
  }

  //-----------------------------------------------------------------
  function drawCanvas( ref, snippet ) {
    //-----------------------------------------------------------------
    if( !ref || !snippet ) return;
    ref.width  = snippet.width ; 
    ref.height = snippet.height;
    const context = ref.getContext('2d');
    context.drawImage( snippet, 0, 0 );
  }

  //-----------------------------------------------------------------
  function displayPageNumber() {
    //-----------------------------------------------------------------
    if( !data.page_number ) return null;

    return <Box sx={_styles.page_number}>Page: {data.page_number}</Box>
  }

  //-----------------------------------------------------------------
  function displayContent() {
    //-----------------------------------------------------------------
    let result = null;

    const type = data.DisplayInfo.Type;

    const STANDARD_PROPS = {
      name            : 'field_txt'     ,
      id              : id              ,
      style           : _styles.input   ,
      field           : data            ,
      value           : content         ,
      has_focus       : has_focus       ,
      onFocus         : onFocus         ,
      onBlur          : onBlur          ,
      onKeyDown       : onKeyDown       ,
      inputRef        : setRefNode      ,
      setMessage      : setMessage      ,
    }

    if( type === 'pagetype' )               result = <Pagetype  { ...STANDARD_PROPS } />;
    else if( type ===  'doctype' )          result = <Doctype   { ...STANDARD_PROPS } />;
    else if( type ===  'selection' )        result = <Selection { ...STANDARD_PROPS } />;
    else if( type ===  'amount' )           result = <Amount    { ...STANDARD_PROPS } />;
    else if( type ===  'date' )             result = <Date      { ...STANDARD_PROPS } />;
    else if( selection_values.length > 0 )  result = <Selection { ...STANDARD_PROPS } />;
    else                                    result = <Standard  { ...STANDARD_PROPS } />;

    return result;
  }

  //#endregion

  return display();
}